import animate from '@play-co/timestep-core/lib/animate';
import View from '@play-co/timestep-core/lib/ui/View';
import uiConfig from 'src/lib/ui/config';
import { animDuration } from 'src/lib/utils';
import ImageScaleView, {
  ImageScaleViewOpts,
} from '@play-co/timestep-core/lib/ui/ImageScaleView';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import buttons from 'src/lib/ui/config/buttons';
import sounds from 'src/lib/sounds';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import StateObserver from 'src/StateObserver';
import { AssetGroup, device } from '@play-co/timestep-core';
import { hideLoading, showLoading } from 'src/state/ui';
import { createEmitter } from 'src/lib/Emitter';
import {
  getMatch2CloseButtonX,
  match2CloseButtonY,
} from './match2PopupCloseButton';

export type Match2BasePopupTitleOptions =
  | {
      type: 'banner';
      localeText?: () => string;
      color?: 'red' | 'green' | 'yellow';
      textSize?: number;
      textOffsetY?: number;
    }
  | { type: 'image'; opts?: ImageScaleViewOpts };

export type Match2BasePopupCTAOptions = {
  localeText?: () => string;
  onClick?: () => void | Promise<void>;
  width?: number;
  positioning?: 'inside' | 'edge';
  offsetY?: number;
};

export interface PopupMatch2BaseOptions {
  superview: View;
  panelImageOpts?: Omit<
    ImageScaleViewOpts,
    'centerAnchor' | 'centerOnOrigin' | 'canHandleEvents' | 'infinite'
  >;
  width?: number;
  height?: number;
  offsetX?: number;
  offsetY?: number;
  zIndex?: number;
  title?: Match2BasePopupTitleOptions;
  cta?: Match2BasePopupCTAOptions;
  tracking: { feature: string; $subFeature: string };
}

/**
 * Legacy init options remain so popup-specific init args can keep their shape.
 * Match2 background-tap dismissal is intentionally ignored by the base popup.
 */
export interface PopupMatch2BaseInitOptions {
  /**
   * Previously allowed backdrop taps to close or advance standard popups.
   * Kept in the option shape so existing popup args do not need churn.
   */
  useClickOutside?: boolean;
}

export default abstract class PopupMatch2Base<
  CloseHandler extends Function = () => void,
> {
  protected root: View;
  protected bg: View;
  protected panelView: View;
  protected panel: ImageScaleView;
  protected title?: LangBitmapFontTextShadowView | ImageScaleView;
  protected buttonClose?: ButtonScaleView;
  protected buttonCta?: ButtonScaleViewWithText;
  protected overlay: View;
  protected titleBanner?: ImageScaleView;

  constructor(
    protected opts: PopupMatch2BaseOptions & { close?: CloseHandler },
  ) {
    this.root = new View({
      width: opts.superview.style.width,
      height: opts.superview.style.height,
      zIndex: opts.zIndex || 9998,
      infinite: true,
    });

    this.bg = new View({
      ...uiConfig.popups.backgroundDarker,
      superview: this.root,
      canHandleEvents: true,
    });

    const width = opts.width ?? 598;
    const height = opts.height ?? 506;

    this.panelView = new View({
      superview: this.root,
      width,
      height,
      x: this.root.style.width * 0.5 + (opts.offsetX || 0),
      y: this.root.style.height * 0.5 + (opts.offsetY || 0),
      centerOnOrigin: true,
      centerAnchor: true,
      zIndex: 1000,
      clipEvents: false,
    });

    this.panel = new ImageScaleView({
      superview: this.panelView,
      image: 'assets/ui/match2/common/panel_match2_popup.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 50, right: 50 },
        vertical: { top: 50, bottom: 50 },
      },
      width,
      height,
      centerAnchor: true,
      zIndex: 1000,
      ...opts.panelImageOpts,
    });

    this.overlay = new View({
      superview: this.root,
      infinite: true,
      zIndex: 100000,
      canHandleEvents: false,
    });

    if (opts.title) {
      if (opts.title.type === 'banner') {
        const banner = new ImageScaleView({
          superview: this.panel,
          image: `assets/ui/match2/common/banner_${
            opts.title.color ?? 'green'
          }.png`,
          width: 390,
          height: 107,
          x: this.panel.style.width * 0.5,
          y: 8,
          centerOnOrigin: true,
          centerAnchor: true,
        });
        this.titleBanner = banner;

        let textShadowColor;

        switch (opts.title.color) {
          case 'red':
            textShadowColor = '#9B2129';
            break;
          case 'green':
            textShadowColor = '#529C21';
            break;
          case 'yellow':
            textShadowColor = '#8c650bff';
            break;
        }

        this.title = new LangBitmapFontTextShadowView({
          superview: banner,
          localeText: opts.title.localeText,
          width: banner.style.width - 80,
          align: 'center',
          verticalAlign: 'center',
          size: opts.title.textSize ?? 46,
          color: '#FFFFFF',
          font: 'Body',
          isRichText: true,
          canHandleEvents: false,
          centerOnOrigin: true,
          x: banner.style.width * 0.5,
          y: opts.title.textOffsetY ?? 20,
          textShadowOffsetX: 0,
          textShadowOffsetY: 5,
          textShadowColor: textShadowColor,
        });
      } else if (opts.title.type === 'image') {
        this.title = new ImageScaleView({
          superview: this.panel,
          centerOnOrigin: true,
          centerAnchor: true,
          x: this.panel.style.width * 0.5,
          ...opts.title.opts,
        });
      }
    }

    if (opts.close) {
      const close = opts.close;

      this.buttonClose = new ButtonScaleView({
        ...buttons.close,
        superview: this.panel,
        x: getMatch2CloseButtonX(this.panel.style.width),
        y: match2CloseButtonY,
        onClick: async () => {
          sounds.playSound('cancel');
          await close();
        },
        tracking: {
          feature: this.opts.tracking.feature,
          $subFeature: `${this.opts.tracking.$subFeature}-match2-close`,
        },
      });
    }

    if (opts.cta) {
      const cta = opts.cta;
      const ctaHeight = 90;
      const ctaWidth = cta.width ?? 235;
      const ctaPositioning = cta.positioning ?? 'inside';

      const onClick = async () => {
        sounds.sfx.play('ok');
        this.setInteractivity(false);
        await cta.onClick?.();
        this.setInteractivity(true);
      };

      this.buttonCta = new ButtonScaleViewWithText({
        ...buttons.secondary,
        fontSize: 40,
        labelOffsetY: -2,
        font: 'Body',
        width: ctaWidth,
        height: ctaHeight,
        x: this.panel.style.width * 0.5 - ctaWidth * 0.5,
        y:
          this.panel.style.height -
          ctaHeight * 0.5 -
          (ctaPositioning === 'inside' ? ctaHeight : 15) +
          (cta.offsetY ?? 0),
        superview: this.panel,
        localeText: cta.localeText ?? (() => 'OKAY'),
        onClick,
        tracking: {
          feature: this.opts.tracking.feature,
          $subFeature: `${this.opts.tracking.$subFeature}-match2-cta`,
        },
      });
    }

    // anchor elements
    createEmitter(this.root, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        this.bg.updateOpts({
          width: screen.width,
          height: screen.height,
          centerOnOrigin: true,
          opacity: 1,
        });
      },
    );
  }

  public getView(): View {
    return this.root;
  }

  public refreshClickOutsidePopup(): void {
    // Background-tap dismissal is disabled; keep legacy callers unchanged.
  }

  public init(_opts: PopupMatch2BaseInitOptions): void {
    this.fadeIn();
  }

  public setTitleText(text: () => string) {
    if (!this.title || this.opts.title?.type !== 'banner') {
      console.warn(
        '[PopupMatch2Base] setTitleText: title is not of type banner',
      );
      return;
    }
    (this.title as LangBitmapFontTextView).localeText = text;
  }

  public setTitleImage(opts: ImageScaleViewOpts) {
    if (!this.title || this.opts.title?.type !== 'image') {
      console.warn(
        '[PopupMatch2Base] setTitleText: title is not of type image',
      );
      return;
    }
    (this.title as ImageScaleView).updateOpts(opts);
  }

  public async fadeIn(interactivity = true) {
    this.setInteractivity(false);
    this.attachRoot();

    this.bg.updateOpts({ opacity: 0 });
    animate(this.bg)
      .clear()
      .then({ opacity: 1 }, animDuration, animate.easeOut);

    this.panelView.updateOpts({ scale: 0 });

    // Just after showing the bg, load required assets
    await this.loadAssets();

    return new Promise<void>((resolve) => {
      animate(this.panelView)
        .clear()
        .wait(animDuration)
        .then({ scale: 1 }, animDuration, animate.easeOut)
        .then(() => this.setInteractivity(interactivity))
        .then(() => resolve());
    });
  }

  public fadeOut(): Promise<void> {
    this.root
      .getSubviews()
      .forEach((view) => view.canHandleEvents(false, true));

    return new Promise<void>((resolve) => {
      this.setInteractivity(false);

      animate(this.bg)
        .clear()
        .wait(0)
        .then({ opacity: 0 }, animDuration, animate.easeOut)
        .then(() => this.detachRoot());

      animate(this.panelView)
        .clear()
        .wait(0)
        .then({ scale: 0 }, animDuration, animate.easeOut)
        .then(() => resolve());
    });
  }

  public setInteractivity(interactable: boolean): void {
    this.overlay.updateOpts({ canHandleEvents: !interactable });
    this.buttonCta?.updateOpts({ canHandleEvents: interactable });
    this.buttonClose?.updateOpts({ canHandleEvents: interactable });
  }

  protected attachRoot() {
    this.opts.superview.addSubview(this.root);
  }

  protected detachRoot() {
    this.root.removeFromSuperview();
  }

  protected getAssetsGroup(): AssetGroup | null {
    // Can be overridden by subclasses
    return null;
  }

  protected async loadAssets() {
    const assetsGroup = this.getAssetsGroup();
    if (!assetsGroup || assetsGroup.isLoaded()) return;

    StateObserver.dispatch(showLoading());
    await assetsGroup.load();
    StateObserver.dispatch(hideLoading());
  }
}
